import { useCallback } from 'react'

import { useForm, useStore } from '@tanstack/react-form'
import { getRouteApi, useNavigate } from '@tanstack/react-router'
import { bankAccountNumberClear } from 'commons/utils'
import { Toast } from 'ui/components'
import { handleFormSubmissionError } from 'ui/components/Form/handleFormSubmissionError'
import { useConfirmation } from 'ui/hooks'

import { hooks } from '../../hooks'
import { BankAccountForm } from '../../types'

const routeApi = getRouteApi('/_app/bank-accounts/$bankAccountId/edit')

const emptyForm: BankAccountForm = {
  active: true,
  closingDate: '',
  id: 0,
  internalTransfersType: 0,
  licenseIds: [],
  notes: '',
  number: '',
  openingDate: ''
}

export const useEdit = () => {
  const navigate = useNavigate({ from: '/bank-accounts/$bankAccountId/edit' })
  const id = routeApi.useParams({ select: (s) => Number(s.bankAccountId) })
  const { data, isFetching } = hooks.useEditPage({ id })
  const returnUrl = routeApi.useSearch({ select: (s) => s.returnUrl })
  const cancelManage = useCallback(() => history.back(), [])

  const {
    mutate: updateBankAccount,
    isPending,
    isSuccess
  } = hooks.useUpdate({
    onError: (error) => handleFormSubmissionError(error, formApi),
    onSuccess: async () => {
      Toast.successSmall('Bank account has been saved.')
      if (returnUrl) {
        navigate({ to: returnUrl, hash: 'bank-accounts' })
      } else {
        navigate({ to: '..' })
      }
    }
  })

  const formApi = useForm({
    defaultValues: data?.form || emptyForm,
    onSubmit: ({ value }) =>
      updateBankAccount({
        ...value,
        number: bankAccountNumberClear(value.number)
      })
  })

  const { handleSubmit } = formApi
  const value = useStore(formApi.store, (s) => s.values)

  const { open: openSaveConfirmation, Confirmation: SaveConfirmation } = useConfirmation({
    message: (
      <>
        This bank account is not assigned to any
        <br />
        license. Do you want to proceed?
      </>
    ),
    onConfirm: handleSubmit,
    confirmationButtonText: 'Submit',
    isConfirmationNegative: true
  })

  const submitForm = value.licenseIds?.length ? handleSubmit : openSaveConfirmation

  return {
    id,
    cancelManage,
    data,
    formApi,
    isFetching,
    isPending,
    isSuccess,
    SaveConfirmation,
    submitForm
  }
}
